import { getItem, type EquipSlot, type ItemDef, type StatBlock, type WeaponType } from '@jianghu/game-data';

export const BAG_SIZE = 30;

/** In-memory mirror of one item instance row. Exactly one of bagSlot / equipSlot is set. */
export interface InvItem {
  id: string;
  defId: string;
  qty: number;
  bagSlot: number | null;
  equipSlot: EquipSlot | null;
  stats: StatBlock;
}

export interface ItemGrant {
  defId: string;
  qty: number;
}

export type InventoryError =
  | 'bag_full'
  | 'unknown_item'
  | 'not_found'
  | 'not_equippable'
  | 'level'
  | 'weapon_type'
  | 'equipped'
  | 'invalid_slot'
  | 'invalid_quantity'
  | 'insufficient';

export const INVENTORY_ERROR_TEXT: Readonly<Record<InventoryError, string>> = {
  bag_full: 'Your bag is full.',
  unknown_item: 'Unknown item.',
  not_found: 'Item not found.',
  not_equippable: 'That item cannot be equipped.',
  level: 'Your level is too low for that item.',
  weapon_type: 'Your sect cannot wield that weapon.',
  equipped: 'Unequip the item first.',
  invalid_slot: 'Invalid slot.',
  invalid_quantity: 'Invalid quantity.',
  insufficient: 'You do not have enough of that item.',
};

export type Plan<T> = ({ ok: true } & T) | { ok: false; error: InventoryError };

export function bagItems(items: readonly InvItem[]): InvItem[] {
  return items.filter((i) => i.bagSlot !== null);
}

export function equippedItems(items: readonly InvItem[]): InvItem[] {
  return items.filter((i) => i.equipSlot !== null);
}

export function freeBagSlots(items: readonly InvItem[], reserved: ReadonlySet<number> = new Set()): number[] {
  const used = new Set<number>();
  for (const i of items) if (i.bagSlot !== null) used.add(i.bagSlot);
  const out: number[] = [];
  for (let s = 0; s < BAG_SIZE; s++) if (!used.has(s) && !reserved.has(s)) out.push(s);
  return out;
}

export function countInBag(items: readonly InvItem[], defId: string): number {
  let n = 0;
  for (const i of items) if (i.bagSlot !== null && i.defId === defId) n += i.qty;
  return n;
}

export interface AddPlan {
  stacks: { itemId: string; add: number }[];
  creates: { defId: string; qty: number; bagSlot: number }[];
}

/** Plans where granted items go: fills existing stacks first, then free slots. All-or-nothing. */
export function planAddItems(items: readonly InvItem[], grants: readonly ItemGrant[]): Plan<AddPlan> {
  const stackRoom = new Map<string, number>();
  const stacks = new Map<string, number>();
  const creates: AddPlan['creates'] = [];
  const reserved = new Set<number>();
  for (const g of grants) {
    const def = getItem(g.defId);
    if (!def) return { ok: false, error: 'unknown_item' };
    if (!Number.isInteger(g.qty) || g.qty <= 0) return { ok: false, error: 'invalid_quantity' };
    let remaining = g.qty;
    if (def.stackSize > 1) {
      for (const it of items) {
        if (remaining <= 0) break;
        if (it.bagSlot === null || it.defId !== def.id) continue;
        const room = stackRoom.get(it.id) ?? def.stackSize - it.qty;
        if (room <= 0) continue;
        const add = Math.min(room, remaining);
        stackRoom.set(it.id, room - add);
        stacks.set(it.id, (stacks.get(it.id) ?? 0) + add);
        remaining -= add;
      }
      // Top up stacks created earlier in this same plan.
      for (const c of creates) {
        if (remaining <= 0) break;
        if (c.defId !== def.id || c.qty >= def.stackSize) continue;
        const add = Math.min(def.stackSize - c.qty, remaining);
        c.qty += add;
        remaining -= add;
      }
    }
    while (remaining > 0) {
      const free = freeBagSlots(items, reserved);
      const slot = free[0];
      if (slot === undefined) return { ok: false, error: 'bag_full' };
      reserved.add(slot);
      const qty = Math.min(def.stackSize, remaining);
      creates.push({ defId: def.id, qty, bagSlot: slot });
      remaining -= qty;
    }
  }
  return { ok: true, stacks: [...stacks].map(([itemId, add]) => ({ itemId, add })), creates };
}

export interface RemovePlan {
  updates: { itemId: string; qty: number }[];
  deletes: string[];
}

/** Plans removal of `qty` units of `defId` from bag stacks (smallest stacks first). */
export function planRemoveItems(items: readonly InvItem[], defId: string, qty: number): Plan<RemovePlan> {
  if (!Number.isInteger(qty) || qty <= 0) return { ok: false, error: 'invalid_quantity' };
  if (countInBag(items, defId) < qty) return { ok: false, error: 'insufficient' };
  const sorted = items
    .filter((i) => i.bagSlot !== null && i.defId === defId)
    .sort((a, b) => a.qty - b.qty || (a.bagSlot as number) - (b.bagSlot as number));
  let remaining = qty;
  const updates: RemovePlan['updates'] = [];
  const deletes: string[] = [];
  for (const it of sorted) {
    if (remaining <= 0) break;
    const take = Math.min(it.qty, remaining);
    remaining -= take;
    if (take === it.qty) deletes.push(it.id);
    else updates.push({ itemId: it.id, qty: it.qty - take });
  }
  return { ok: true, updates, deletes };
}

export function checkEquippable(
  def: ItemDef | undefined,
  level: number,
  sectWeapon: WeaponType,
): InventoryError | null {
  if (!def) return 'unknown_item';
  if (!def.equipSlot) return 'not_equippable';
  if (level < def.levelRequired) return 'level';
  if (def.equipSlot === 'weapon' && def.weaponType !== sectWeapon) return 'weapon_type';
  return null;
}

export interface SlotMove {
  itemId: string;
  bagSlot: number | null;
  equipSlot: EquipSlot | null;
}

/** Equip a bag item; any item already in that equipment slot swaps into the vacated bag slot. */
export function planEquip(
  items: readonly InvItem[],
  itemId: string,
  level: number,
  sectWeapon: WeaponType,
): Plan<{ moves: SlotMove[] }> {
  const it = items.find((i) => i.id === itemId);
  if (!it || it.bagSlot === null) return { ok: false, error: 'not_found' };
  const def = getItem(it.defId);
  const err = checkEquippable(def, level, sectWeapon);
  if (err) return { ok: false, error: err };
  const slot = (def as ItemDef).equipSlot as EquipSlot;
  const current = items.find((i) => i.equipSlot === slot);
  const moves: SlotMove[] = [];
  if (current) moves.push({ itemId: current.id, bagSlot: it.bagSlot, equipSlot: null });
  moves.push({ itemId: it.id, bagSlot: null, equipSlot: slot });
  return { ok: true, moves };
}

export function planUnequip(items: readonly InvItem[], slot: EquipSlot): Plan<{ moves: SlotMove[] }> {
  const it = items.find((i) => i.equipSlot === slot);
  if (!it) return { ok: false, error: 'not_found' };
  const free = freeBagSlots(items)[0];
  if (free === undefined) return { ok: false, error: 'bag_full' };
  return { ok: true, moves: [{ itemId: it.id, bagSlot: free, equipSlot: null }] };
}

export interface MovePlan {
  moves: SlotMove[];
  /** Stack merge: quantity changes. A source reduced to 0 is deleted. */
  qty: { itemId: string; qty: number }[];
  deletes: string[];
}

/** Drag-and-drop inside the bag: move to empty slot, merge identical stacks, or swap. */
export function planMoveBag(items: readonly InvItem[], from: number, to: number): Plan<MovePlan> {
  if (!validSlot(from) || !validSlot(to) || from === to) return { ok: false, error: 'invalid_slot' };
  const src = items.find((i) => i.bagSlot === from);
  if (!src) return { ok: false, error: 'not_found' };
  const dst = items.find((i) => i.bagSlot === to);
  if (!dst)
    return { ok: true, moves: [{ itemId: src.id, bagSlot: to, equipSlot: null }], qty: [], deletes: [] };
  const def = getItem(src.defId);
  if (def && def.stackSize > 1 && dst.defId === src.defId && dst.qty < def.stackSize) {
    const moved = Math.min(def.stackSize - dst.qty, src.qty);
    const left = src.qty - moved;
    return {
      ok: true,
      moves: [],
      qty: [{ itemId: dst.id, qty: dst.qty + moved }, ...(left > 0 ? [{ itemId: src.id, qty: left }] : [])],
      deletes: left > 0 ? [] : [src.id],
    };
  }
  return {
    ok: true,
    moves: [
      { itemId: src.id, bagSlot: to, equipSlot: null },
      { itemId: dst.id, bagSlot: from, equipSlot: null },
    ],
    qty: [],
    deletes: [],
  };
}

export function planSplit(
  items: readonly InvItem[],
  slot: number,
  qty: number,
): Plan<{ itemId: string; remain: number; newSlot: number }> {
  const it = items.find((i) => i.bagSlot === slot);
  if (!it) return { ok: false, error: 'not_found' };
  if (!Number.isInteger(qty) || qty <= 0 || qty >= it.qty) return { ok: false, error: 'invalid_quantity' };
  const free = freeBagSlots(items)[0];
  if (free === undefined) return { ok: false, error: 'bag_full' };
  return { ok: true, itemId: it.id, remain: it.qty - qty, newSlot: free };
}

function validSlot(s: number): boolean {
  return Number.isInteger(s) && s >= 0 && s < BAG_SIZE;
}

/** Rolls the concrete stats for a new item instance from its definition's ranges. */
export function rollItemStats(def: ItemDef, random: () => number): StatBlock {
  const out: StatBlock = {};
  if (!def.stats) return out;
  for (const [k, range] of Object.entries(def.stats) as [keyof StatBlock, [number, number]][]) {
    const [min, max] = range;
    const v = min + (max - min) * random();
    // Percent stats keep 3 decimals, flat stats are integers.
    out[k] = max < 1 ? Math.round(v * 1000) / 1000 : Math.round(v);
  }
  return out;
}
